import ErrorIcon from '@mui/icons-material/Error';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { type Dispatch, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useSavedPaymentMethod } from 'reservations/booking/providers/BookingProvider';
import { NewCardForm } from 'reservations/payments/components/NewCardForm';
import { getReadableCardType } from 'reservations/payments/helpers/getReadableCardType';
import { isCardExpired } from 'reservations/payments/helpers/isCardExpired';
import { useDinerPaymentMethods } from 'reservations/payments/hooks/useDinerPaymentMethods';

export const PaymentForm = ({
  setHandleSaveCard
}: {
  setHandleSaveCard: Dispatch<() => void>;
}) => {
  const { setValue } = useFormContext();
  const [cardDetails, date] = useWatch({
    name: ['cardDetails', 'date']
  });
  const { defaultPaymentMethod } = useDinerPaymentMethods();
  const previouslySavedCard = useSavedPaymentMethod();
  const hasUnsavedCard = !!cardDetails;
  const hasPreviouslySavedCard = !!previouslySavedCard;
  const hasDefaultPaymentMethod = !!defaultPaymentMethod;

  let cardType,
    cardLast4,
    cardId: string,
    cardExpired = false;

  // We follow a heirarchy to determine which card details we surface
  switch (true) {
    // If a diner has enter new card details we surface those, even though technically
    // a saved payment method has not been created yet
    case hasUnsavedCard:
      cardType = cardDetails?.brand;
      cardLast4 = cardDetails?.last4;
      cardExpired = isCardExpired(
        cardDetails?.exp_month,
        cardDetails?.exp_year,
        date
      );
      break;
    // Otherwise we surface the card that's already associated wiith the visit if one exists
    case hasPreviouslySavedCard:
      cardType = previouslySavedCard?.card_type;
      cardLast4 = previouslySavedCard?.card_last4;
      cardId = previouslySavedCard?.id!;
      cardExpired = isCardExpired(
        previouslySavedCard?.card_expiration_month!,
        previouslySavedCard?.card_expiration_year!,
        date
      );
      break;
    // Finally we surface the most recently used card associated with the diner
    default:
      cardType = defaultPaymentMethod?.cardType;
      cardLast4 = defaultPaymentMethod?.cardLast4;
      cardId = defaultPaymentMethod?.id;
      cardExpired = isCardExpired(
        defaultPaymentMethod?.cardExpMonth,
        defaultPaymentMethod?.cardExpYear,
        date
      );
  }

  // If any of these are true we render the payments selection
  const renderPaymentSelection =
    hasUnsavedCard || hasPreviouslySavedCard || hasDefaultPaymentMethod;

  const [replaceCard, setReplaceCard] = useState(cardExpired);

  useEffect(() => {
    if (renderPaymentSelection && cardId && !cardExpired) {
      setValue('savedPaymentMethod', cardId);
    }
  }, []);

  return (
    <>
      {renderPaymentSelection && (
        <FormControl data-testid="payment-form">
          <RadioGroup defaultValue={cardExpired ? 'replace' : 'select'}>
            <FormControlLabel
              value="select"
              checked={!replaceCard}
              onChange={() => {
                setHandleSaveCard(() =>
                  hasUnsavedCard
                    ? // If there's an unsaved card, we don't need to do anything
                      () => {}
                    : // Otherwise, we set saved payment method
                      () => setValue('savedPaymentMethod', cardId)
                );
                setReplaceCard(false);
              }}
              control={
                <Radio
                  disabled={cardExpired}
                  data-testid="select-saved-card-radio"
                  sx={theme => ({
                    ...(cardExpired && {
                      '&.Mui-disabled': {
                        color: theme.palette.error.main,
                        opacity: 0.4
                      }
                    })
                  })}
                />
              }
              label={
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Typography
                    pr="8px"
                    sx={theme => ({
                      ...(cardExpired && {
                        color: theme.palette.error.main,
                        opacity: 0.4
                      })
                    })}
                  >
                    {getReadableCardType(cardType)} {cardLast4}
                  </Typography>
                  {cardExpired && (
                    <>
                      <ErrorIcon color="error" />
                      <Typography pl="4px" color="error">
                        Your card has expired
                      </Typography>
                    </>
                  )}
                </Box>
              }
            />
            <FormControlLabel
              value="replace"
              onChange={() => setReplaceCard(true)}
              checked={replaceCard}
              control={<Radio data-testid="replace-saved-card-radio" />}
              label="Replace your card"
              sx={{ pb: '20px' }}
            />
          </RadioGroup>
        </FormControl>
      )}
      {/* If there are no payment methods to surface we render the credit card form fields by themselves */}
      {(replaceCard || !renderPaymentSelection) && (
        <NewCardForm setHandleSaveCard={setHandleSaveCard} />
      )}
    </>
  );
};
